import React from 'react';
import { Button, createStyles, FormHelperText, Grid, Icon, makeStyles, Typography } from '@material-ui/core';
import { Action, Column, Options } from 'material-table';

import { ExtraParameterModel, extraParameterModelInitial } from '../../../../Models/ExtraParameterModel';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { TableCommon } from '../../../../shared/components/TableCommon';
import { TableEmpty } from '../../../../shared/components/TableEmpty';
import { ExtraParameterFieldType } from '../../../../shared/enum/ExtraParameterFieldType';
import { ClientContext } from '../../ClientContext';
import { ClientErrorContext } from '../../ClientErrorContext';
import { EditAction } from '../../../../shared/enum/EditAction';
import { getTableColumn } from '../../../../shared/operators';
import { ClientExtraParameterModal } from '../components/ClientExtraParameterModal';
import { ExtraParameterFieldTypes, IExtraParameterFieldType } from '../model/ExtraParameterFieldType';
import { ClientExtraParameterContext } from '../ClientExtraParameterContext';

const useStyles = makeStyles(() => createStyles({
    cardHeight: {
        overflow: 'hidden',
        maxHeight: '240px'
    },
    textButton: {
        paddingLeft: 0,
        '&:hover': {
            textDecorationLine: 'underline',
        },
    },
}));


export function ClientExtraParameter() {
    const classes = useStyles();
    
    const clientContext = React.useContext(ClientContext); 
    const clientErrorContext = React.useContext(ClientErrorContext); 

    const [currentExtraParameter, setCurrentExtraParameter] = React.useState<ExtraParameterModel>(extraParameterModelInitial);
    const [selectedExtraParameterFieldType, setSelectedExtraParameterFieldType] = React.useState<IExtraParameterFieldType | null>(null);
    const [actionFieldIndex, setActionFieldIndex] = React.useState<number>(-1);
    const [isInEditMode, setIsInEditMode] = React.useState(false);
    
    const [isExtraParameterModalOpen, setIsExtraParameterModalOpen] = React.useState(false);
    const [isConfirmDeletionOfExtraParameterModalOpen, setIsConfirmDeletionOfExtraParameterModalOpen] = React.useState(false);

    React.useEffect(() => {
        if (isInEditMode) {
            const fieldType = ExtraParameterFieldTypes.find(fieldType => fieldType.value == currentExtraParameter.type);
            setSelectedExtraParameterFieldType(fieldType ? fieldType : null);
        }
    }, [currentExtraParameter.type]);

    React.useEffect(() => {
        setCurrentExtraParameter({ ...currentExtraParameter,
            type: selectedExtraParameterFieldType ? selectedExtraParameterFieldType.value : null,
            answers: selectedExtraParameterFieldType && selectedExtraParameterFieldType.value == ExtraParameterFieldType.TEXT
                ? ''
                : currentExtraParameter.answers
        });
    }, [selectedExtraParameterFieldType]);

    const tableExtraParametersColumns: Column<ExtraParameterModel>[] = [
        getTableColumn('Pergunta', 'description', 'string', 'left', { width: '25%' }, 'asc', true),
        {
            title: 'Tipo de Campo',
            type: 'string',
            align: 'left',
            sorting: true,
            field: 'type',
            render: rowData => {
                const fieldType = ExtraParameterFieldTypes.find(fieldType => fieldType.value == rowData.type);
                return fieldType ? fieldType.name : '';},
            customFilterAndSearch: (term: string, rowData) => {
                const fieldType = ExtraParameterFieldTypes.find(fieldType => fieldType.value == rowData.type);
                return fieldType ? fieldType.name.toLowerCase().includes(term.toLowerCase()) : false;
            },
            cellStyle: { width: '20%' },
        },
        {
            title: 'Respostas',
            type: 'string',
            align: 'left',
            sorting: true,
            field: 'answers',
            render: rowData => rowData.answers?.replaceAll(';', '; '),
            cellStyle: { width: '35%' },
        },
        {
            title: 'Status',
            type: 'string',
            align: 'left',
            sorting: true,
            field: 'active',
            render: rowData => rowData.active ? 'Ativo' : 'Inativo',
            cellStyle: { width: '10%' },
        },
    ];
    
    const tableExtraParametersOptions: Options<ExtraParameterModel> = {
        filtering: true,
        sorting: true,
        showTitle: false,
        toolbar: false,
        headerStyle: {
            fontSize: '14px',
            whiteSpace: 'nowrap'
        },
        pageSizeOptions: [5, 10, 15],
        pageSize: 5,
    };
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tableExtraParametersActions: (Action<any> | ((rowData: any) => Action<any>))[] = [
        {
            icon: 'edit',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Editar mensagem chave',
            onClick: (_, rowData) => { handleEditButton(rowData, rowData.tableData.id); }
        },
        (rowData: ExtraParameterModel) => ({
            icon: 'delete_outline',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Apagar mensagem chave',
            onClick: (_, rowData) => { setActionFieldIndex(rowData.tableData.id); setIsConfirmDeletionOfExtraParameterModalOpen(true); },
            disabled: rowData.editAction != EditAction.INSERT,
            hidden: rowData.editAction != EditAction.INSERT,
        }),
    ];


    const handleEditButton = (extraParameter: ExtraParameterModel, index: number) => {
        setIsInEditMode(true);
        setCurrentExtraParameter(extraParameter);
        setActionFieldIndex(index);
        setIsExtraParameterModalOpen(true);
    };

    const handleDeleteButton = (index: number) => {
        const extraParameters = [ ...clientContext.extraParameters.filter((e) => e.editAction != EditAction.DELETE && e.active) ];

        if (extraParameters[index].editAction == EditAction.INSERT)
            extraParameters.splice(index, 1);
        else
            extraParameters[index].editAction = EditAction.DELETE;

        clientContext.setExtraParameters(extraParameters);
        setIsConfirmDeletionOfExtraParameterModalOpen(false);
        setActionFieldIndex(-1);
    };

    const getAddNewButton = () => {
        return (
            <Grid item xs={12} >
                <Button
                    onClick={() => setIsExtraParameterModalOpen(true)}
                    className={classes.textButton}
                    startIcon={<Icon color='primary'>add</Icon>} >
                    Adicionar Mensagem Chave
                </Button>
            </Grid>
        );
    };

    const getExtraParameterList = () => {
        return (
            <>
                {!clientContext.extraParameters.length ||
                    clientContext.extraParameters.filter((e) => e.editAction != EditAction.DELETE).length == 0 
                    ? (
                        <Grid item xs={12} className={classes.cardHeight}>
                            <TableEmpty 
                                displayText="Nenhuma mensagem chave cadastrada" 
                                cssProperties={{ 
                                    minHeight: '150px', 
                                    errorBorder: (clientErrorContext.extraParameterNotDefinedError &&
                                        !(clientContext.extraParameters &&
                                            clientContext.extraParameters.filter((e) => e.editAction != EditAction.DELETE && e.active).length > 0))  }} /> 
                            { (clientErrorContext.extraParameterNotDefinedError && !(clientContext.extraParameters
                                    && clientContext.extraParameters.filter((e) => e.editAction != EditAction.DELETE && e.active).length > 0)) &&
                                <FormHelperText error>
                                    Você precisa inserir pelo menos uma mensagem chave para este cliente.
                                </FormHelperText>
                            }
                        </Grid>
                    ) : (
                        <Grid item xs={12} >
                            <TableCommon
                                tableData={clientContext.extraParameters.filter((e) => e.editAction != EditAction.DELETE)}
                                tableColumns={tableExtraParametersColumns}
                                tableActions={tableExtraParametersActions}
                                extraOptions={tableExtraParametersOptions}
                                emptyDataSourceMessage='Nenhuma mensagem chave cadastrada' />
                            { (clientErrorContext.extraParameterNotDefinedError && !(clientContext.extraParameters
                                        && clientContext.extraParameters.filter((e) => e.editAction != EditAction.DELETE && e.active).length > 0)) &&

                                <FormHelperText error>
                                    Você precisa inserir pelo menos uma mensagem chave ativa para este cliente.
                                </FormHelperText>
                            }
                        </Grid>
                    )
                }
            </>
        );
    };

    return (
        <ClientExtraParameterContext.Provider value={{
            currentExtraParameter, setCurrentExtraParameter,
            selectedExtraParameterFieldType, setSelectedExtraParameterFieldType,
            actionFieldIndex, setActionFieldIndex,
            isInEditMode, setIsInEditMode
        }}>
            <Grid container direction="column" spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="subtitle1">
                        Mensagens chave
                    </Typography>
                </Grid>
                {getAddNewButton()}
                {getExtraParameterList()}
            </Grid>
           
            <ClientExtraParameterModal showDialog={isExtraParameterModalOpen} setShowDialog={setIsExtraParameterModalOpen} />

            <BasicConfirmDialog
                title="Excluir mensagem chave?"
                description={'Ao excluir esta mensagem chave, ' + 
                    'ela não ficará mais disponível para a análise de mensagens chave de futuras classificações.'}
                handleFirstButton={() => handleDeleteButton(actionFieldIndex)}
                handleSecondButton={() => setIsConfirmDeletionOfExtraParameterModalOpen(false)}
                textFirstButton="Excluir"
                textSecondButton="Voltar"
                reverseButtons={true}
                open={isConfirmDeletionOfExtraParameterModalOpen} />
        </ClientExtraParameterContext.Provider>
    );
}